<template>
  <div>
    <div
      v-if="otherUser"
      class="user"
    >
      <div class="row responsive">
        <avatar
          :user="currentUser"
          :size="150"
          @click="page = 'posts'"
        />
        <div class="column">
          <div class="profile-name">
            {{ currentUser.first_name }} {{ currentUser.last_name }}
          </div>
          <div
            v-if="currentUser.badges"
            class="badges"
          >
            <div
              v-for="badge of Object.keys(currentUser.badges)"
              :key="badge"
              class="badge"
            >
              <star-icon v-if="badge == 'founder'" />
              {{ badge }}
            </div>
          </div>
          <div class="profile-bio">
            <pre>{{ currentUser.bio }}</pre>
          </div>
          <!-- NAV -->
          <div class="row actions">
            <!-- POSTS -->
            <button
              type="button"
              class="btn btn-transparent btn-sm"
              :class="{ 'active': page === 'posts' }"
              @click="page = 'posts'"
            >
              <div>Posts</div>
              <div>{{ otherUser.posts_count }}</div>
            </button>
            <!-- FOLLOWERS -->
            <button
              type="button"
              class="btn btn-transparent btn-sm"
              :class="{ 'active': page === 'followers' }"
              @click="page = 'followers'"
            >
              <div>Followers</div>
              <div>{{ otherUser.followers_count }}</div>
            </button>
            <!-- FOLLOWING -->
            <button
              type="button"
              class="btn btn-transparent btn-sm"
              :class="{ 'active': page === 'following' }"
              @click="page = 'following'"
            >
              <div>Following</div>
              <div>{{ otherUser.following_count }}</div>
            </button>
            <!-- BLOCKING -->
            <button
              v-if="isUser"
              type="button"
              class="btn btn-transparent btn-sm"
              :class="{ 'active': page === 'blocking' }"
              @click="page = 'blocking'"
            >
              <div>Blocking</div>
              <div>{{ otherUser.blocking_count }}</div>
            </button>
          </div>
        </div>

        <button
          v-if="isUser"
          type="button"
          class="btn btn-sm btn-update"
          @click="$router.push('/update-profile')"
        >
          Edit Profile
        </button>
      </div>

      <div
        v-if="!isUser"
        class="buttons mt"
      >
        <router-link
          :to="{name: 'message_profile', params: {id: currentUser.id}}"
          class="btn btn-primary btn-sm btn-message"
        >
          Message
        </router-link>
        <follow-unfollow
          :other-user="otherUser"
        />
        <block-unblock
          :other-user="otherUser"
        />
      </div>

      <div class="mt">
        <template v-if="page === 'posts'">
          <listing-loader
            v-if="userData"
            page="profile"
            :user="currentUser.id"
            :show-new-post-card="isUser"
          />
        </template>
        <template v-else-if="page === 'following'">
          <following
            :id="currentUser.id"
          />
        </template>
        <template v-else-if="page === 'followers'">
          <followers
            :id="currentUser.id"
          />
        </template>
        <template v-else-if="page === 'blocking'">
          <blocking
            :id="currentUser.id"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue';
import FollowUnfollow from '@/components/FollowUnfollow.vue';
import BlockUnblock from '@/components/BlockUnblock.vue';
import ListingLoader from '@/components/ListingLoader.vue';
import Following from '@/components/Following.vue';
import Followers from '@/components/Followers.vue';
import Blocking from '@/components/Blocking.vue';

export default {
  name: 'Profile',
  components: {
    Avatar,
    FollowUnfollow,
    BlockUnblock,
    ListingLoader,
    Following,
    Followers,
    Blocking,
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      otherUser: false,
      page: 'posts',
    };
  },
  computed: {
    userData() {
      return this.$store.state.userData;
    },
    isUser() {
      return this.userData.id === Number(this.id);
    },
    currentUser() {
      if (this.isUser) {
        return this.userData;
      }
      return this.otherUser;
    },
  },
  watch: {
    id() {
      this.page = 'posts';
      this.loadUser();
    },
  },
  created() {
    this.loadUser();
  },
  methods: {
    async loadUser() {
      const responseData = await this.api({
        url: `users/${this.id}`,
        method: 'GET',
      });

      if (responseData.status === 200) {
        this.otherUser = responseData.body;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-name {
  font-size: 1.2rem;
  font-weight: bold;
  padding-top: 15px;
}
.profile-bio {
  width: 600px;
  max-width: 600px;
  font-size: 85%;
  word-wrap: break-word;
}
.buttons {
  text-align: right;
  margin-bottom: 20px;
}
.column {
  padding-left: 15px;
}
.actions {
  margin: 20px 0px;
}
.btn-update {
  position: relative;
  color: var(--gray-darkest);
  height: 30px;
  right: 80px;
  top: 15px;

  &:hover {
    background-color: var(--gray-dark);
  }
}

.actions {
  justify-content: space-around;

  button {
    line-height: 1.3;
    text-align: center;
    color: var(--gray-darker);
    padding-bottom: 5px;
    border-bottom: 3px solid transparent;

    div:nth-child(2) {
      font-weight: 600;
      width: 100%;
      text-align: center;
      color: var(--gray-darkest);
    }

    &:hover {
      border-bottom: 3px solid var(--gray-darker);
    }
  }
}

.active {
  border-bottom: 3px solid var(--gray-darkest) !important;
}

.mt {
  margin-top: 32px !important;
}

.card {
  padding: 18px 8px;
}

.badges {
  margin-top: -4px;
}
.badge {
  padding: 2px 12px 2px 6px;
  font-size: 11px;
  background-color: var(--gray-light);
  color: var(--blue-dark);
  border-radius: 10px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}
.badge .star-icon {
  padding: 0;
  margin: 0;
  margin-right: 5px;
  margin-top: 2px;
  border: 2px solid transparent;
}

@media (max-width: 1024px) {
  .btn-update {
    position: absolute;
    top: calc(var(--header-height) + 50);
    right: 20px;
  }

  .profile-bio {
    width: 100%;
    max-width: 100%;
  }

  .column {
    padding-left: 0px;
  }

  .buttons {
    text-align: center;
  }
}
</style>
